<template>
    <div
        class="scrollable"
        :class="{
            'scrollable--vertical': !horizontal,
            'scrollable--horizontal': horizontal,
            'scrollable--external': external,
            'scrollable--has-edge-fades': edgeFades,
            'scrollable--can-scroll-start': edgeFades && canScrollStart,
            'scrollable--can-scroll-end': edgeFades && canScrollEnd,
            'scrollable--no-scroll': !hasScrollbar,
        }"
        :style="{
            '--offset-top': offsetTopInPx,
            '--fade-width': fadeWidthPx,
        }"
    >
        <div
            ref="content"
            v-bind="contentProps"
            :id="contentId"
            class="scrollable__content"
            :class="contentClass"
            :role="contentRole"
            :aria-label="contentAriaLabel ?? undefined"
            :tabindex="hasScrollbar ? 0 : -1"
            @scroll.passive="onScroll"
        >
            <slot />

            <InfiniteScrollSentinel
                :offset="reachEndOffset"
                :root="contentRef"
                @visible="emit('scrollReachEnd')"
            />
        </div>

        <div
            v-show="!hideScrollbar && hasScrollbar"
            class="scrollable__scrollbar scrollbar"
            data-cy="scrollable-scrollbar"
            :class="[{
                'scrollable__scrollbar--active': isActive,
                'scrollbar--active': isActive,
            }]"
        >
            <div
                class="scrollbar__track"
                aria-hidden="true"
                tabindex="-1"
                :style="trackStyles"
                @mousedown="startTrackMove"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
} from 'vue';
import * as scrollHelper from '../../../scripts/helpers/scroll';
import InfiniteScrollSentinel from './InfiniteScroll/InfiniteScrollSentinel.vue';

const SENTINEL_HEIGHT = 1;
const SENTINEL_WIDTH = 1;

type ClassValue = string | Record<string, boolean> | ClassValue[] | null;

type Props = {
    horizontal?: boolean,
    contentClass?: ClassValue,
    offsetTop?: number;
    external?: boolean,
    hideScrollbar?: boolean,
    wheelScroll?: boolean,
    reachEndOffset?: string,
    edgeFades?: boolean | number,
    persistScroll?: string,
    contentId?: string,
    contentRole?: string,
    contentAriaLabel?: string | null,
    contentProps?: Record<string, unknown>,
}

type Emits = {
    scrollReachEnd: [],
}

type Point = { x: number, y: number };

const emit = defineEmits<Emits>();

const {
    contentClass = null,
    external = false,
    horizontal = false,
    offsetTop = 0,
    hideScrollbar = false,
    wheelScroll = true,
    reachEndOffset = '250px',
    edgeFades = false,
    persistScroll = undefined,
    contentId = undefined,
    contentRole = undefined,
    contentAriaLabel = undefined,
    contentProps = undefined,
} = defineProps<Props>();

const contentRef = useTemplateRef<HTMLElement>('content');

const savePersistedScroll = (): void => {
    if (!persistScroll || !contentRef.value) {
        return;
    }
    const value = horizontal ? contentRef.value.scrollLeft : contentRef.value.scrollTop;
    sessionStorage.setItem(persistScroll, String(value));
};

const restorePersistedScroll = (): void => {
    if (!persistScroll || !contentRef.value) {
        return;
    }
    const saved = sessionStorage.getItem(persistScroll);
    if (saved === null) {
        return;
    }
    if (horizontal) {
        contentRef.value.scrollLeft = Number(saved);
    } else {
        contentRef.value.scrollTop = Number(saved);
    }
};

let observer: MutationObserver|null = null;
let resizeObserver: ResizeObserver | null = null;

const trackSize = ref<number>(0);
const trackOffset = ref(0);
const hasScrollbar = ref(true);
const trackMoveLocation = ref<Point | null>(null);

const canScrollStart = ref(false);
const canScrollEnd = ref(false);
let rafId: number | null = null;

const trackStyles = computed<Record<string, string>>(() => {
    const sizeProperty = horizontal ? 'width' : 'height';
    const offsetProperty = horizontal ? 'margin-left' : 'margin-top';

    return {
        [sizeProperty]: `${trackSize.value}px`,
        [offsetProperty]: `${trackOffset.value}px`,
    };
});
const isActive = computed(() => trackMoveLocation.value !== null);
const offsetTopInPx = computed(() => `${offsetTop}px`);

const fadeWidth = computed(() => {
    if (!edgeFades) {
        return 0;
    }
    return typeof edgeFades === 'number' ? edgeFades : 25;
});

const fadeWidthPx = computed(() => `${fadeWidth.value}px`);


const getScroll = (): number => {
    if (!contentRef.value) {
        return 0;
    }

    return horizontal
        ? contentRef.value?.scrollLeft
        : contentRef.value?.scrollTop;
};

const getSize = (): number => {
    if (!contentRef.value) {
        return 0;
    }

    return horizontal
        ? contentRef.value.offsetWidth
        : contentRef.value.offsetHeight - offsetTop;
};

const getScrollableSize = (): number => {
    if (!contentRef.value) {
        return 0;
    }

    return horizontal
        ? contentRef.value.scrollWidth - SENTINEL_WIDTH
        : contentRef.value.scrollHeight - offsetTop - SENTINEL_HEIGHT;
};

const refreshTrackStyles = (): void => {
    if (!contentRef.value) {
        return;
    }

    const size = getSize();
    const scrollableSize = getScrollableSize();

    const trackPercentHeight = size / scrollableSize;
    trackSize.value = trackPercentHeight * size;

    const scroll = getScroll();

    const maxOffset = size - trackSize.value;
    const maxScroll = scrollableSize - size;
    const scrollToTrackOffsetPercent = maxOffset / maxScroll || 0;
    trackOffset.value = scroll * scrollToTrackOffsetPercent;

    hasScrollbar.value = scrollableSize > size;
};

const updateEdgeFadeState = (): void => {
    if (!edgeFades || !contentRef.value) {
        return;
    }

    const scroll = getScroll();
    const size = getSize();
    const scrollableSize = getScrollableSize();

    const epsilon = Math.max(1, Math.ceil(window.devicePixelRatio || 1));

    canScrollStart.value = scroll > epsilon;

    canScrollEnd.value = scroll < scrollableSize - size - epsilon;
};


const onScroll = (): void => {
    refreshTrackStyles();
    savePersistedScroll();

    if (edgeFades) {
        if (rafId !== null) {
            cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
            updateEdgeFadeState();
            rafId = null;
        });
    }
};

const startTrackMove = (event: MouseEvent) => {
    const { screenX, screenY } = event;
    trackMoveLocation.value = { x: screenX, y: screenY };
    document.body.style.userSelect = 'none';
};

const calcScrollPercent = (size: number, scrollable: number): number => {
    const maxOffset = size - trackSize.value;
    const maxScroll = scrollable - size;

    return maxScroll / maxOffset;
};

const stopTrackMove = (): void => {
    document.body.style.userSelect = '';
    if (trackMoveLocation.value === null) {
        return;
    }

    trackMoveLocation.value = null;
};

const onTrackMove = (event: MouseEvent): void => {
    const { screenX, screenY } = event;
    if (trackMoveLocation.value === null || trackSize.value === null) {
        return;
    }

    // avoid element selection
    event.preventDefault();
    event.stopPropagation();

    const { x, y } = trackMoveLocation.value;
    trackMoveLocation.value = { x: screenX, y: screenY };

    const offsetY = screenY - y;
    const offsetX = screenX - x;
    const offset = horizontal ? offsetX : offsetY;

    const size = getSize();
    const scrollableSize = getScrollableSize();
    const scrollPercent = calcScrollPercent(size, scrollableSize);

    const scroll = offset * scrollPercent;
    const scrollRounded = Math.round(scroll);

    const scrollByX = horizontal ? scrollRounded : 0;
    const scrollByY = horizontal ? 0 : scrollRounded;
    contentRef.value?.scrollBy(scrollByX, scrollByY);
};

const createRefreshObserver = (): MutationObserver => {
    const mutationObserver = new MutationObserver(refreshTrackStyles);

    if (contentRef.value) {
        mutationObserver.observe(
            contentRef.value,
            {
                childList: true,
                characterData: true,
                subtree: true,
            },
        );
    }

    return mutationObserver;
};

const onWheelScroll = (event: WheelEvent) => {
    if (horizontal && contentRef.value) {
        const isMouseWheel = Math.abs(event.deltaY) > Math.abs(event.deltaX);

        if (isMouseWheel) {
            event.preventDefault();
            contentRef.value.scrollLeft += event.deltaY;
        } else {
            contentRef.value.scrollLeft += event.deltaX;
        }
    }
};

onMounted(() => {
    refreshTrackStyles();
    observer = createRefreshObserver();

    const handleResize = () => {
        refreshTrackStyles();
        if (edgeFades) {
            updateEdgeFadeState();
        }
    };

    resizeObserver = new ResizeObserver(handleResize);
    if (contentRef.value) {
        resizeObserver.observe(contentRef.value);

        // todo handle children mutation
        for (const child of contentRef.value.children) {
            resizeObserver.observe(child);
        }
    }

    document.addEventListener('mousemove', onTrackMove);
    document.addEventListener('mouseup', stopTrackMove);

    if (wheelScroll) {
        contentRef.value?.addEventListener('wheel', onWheelScroll);
    }

    if (edgeFades) {
        updateEdgeFadeState();
    }

    restorePersistedScroll();
});

onUnmounted(() => {
    if (observer) {
        observer.disconnect();
    }

    if (resizeObserver) {
        resizeObserver.disconnect();
    }

    document.removeEventListener('mousemove', onTrackMove);
    document.removeEventListener('mouseup', stopTrackMove);

    contentRef.value?.removeEventListener('wheel', onWheelScroll);

    if (rafId !== null) {
        cancelAnimationFrame(rafId);
    }
});

const scrollToStart = () => {
    if (!contentRef.value) {
        return;
    }

    contentRef.value.scrollTo({
        top: horizontal ? undefined : 0,
        left: horizontal ? 0 : undefined,
        behavior: 'auto',
    });
};

const scrollToEnd = () => {
    if (!contentRef.value) {
        return;
    }

    scrollHelper.scrollToEnd(contentRef.value, horizontal);
};

const scrollStep = (step: number, behavior: 'auto' | 'smooth' = 'smooth') => {
    const value = getScroll() + step;

    contentRef.value?.scrollTo({
        behavior,
        top: horizontal ? 0 : value,
        left: horizontal ? value : 0,
    });
};

defineExpose({
    scrollToStart,
    scrollToEnd,
    scrollStep,
    contentElement: contentRef,
});

</script>

<style lang="scss" scoped>
@import '../../../styles/abstracts/spacings';
@import '../../../styles/abstracts/z-indexes';

$scrollbar-track-size: 0.5rem;
$scrollbar-track-size-on-hover: $scrollbar-track-size;
$scrollbar-padding: 0.125rem;

@mixin hide-default-scrollbar() {
    -ms-overflow-style: none; // IE

    scrollbar-width: none; // Firefox

    &::-webkit-scrollbar {
        display: none; // Chrome, Safari
    }
}

.scrollable {
    position: relative;

    &--vertical {
        height: 100%;
    }

    &--horizontal {
        width: 100%;
    }

    &--external {
        $scrollbar-external-offset: $scrollbar-track-size-on-hover + $spacing-lm;
        flex-grow: 1;

        width: calc(100% + #{$scrollbar-external-offset});
        padding-right: $scrollbar-external-offset;
    }

    &__content {
        @include hide-default-scrollbar;

        display: flex;

        flex-direction: column;

        height: 100%;

        overscroll-behavior: contain;
    }

    &--no-scroll > &__content {
        overscroll-behavior: auto;
    }

    &--vertical > &__content {
        overflow-x: hidden;
        overflow-y: auto;
    }

    &--horizontal > &__content {
        overflow-x: auto;

        overscroll-behavior-y: auto;
    }

    &__scrollbar {
        opacity: 0;

        transition: opacity 0.5s;
    }

    &__scrollbar--active,
    &:hover > &__scrollbar {
        opacity: 1;
    }

    &--has-edge-fades {
        &::before {
            position: absolute;
            z-index: 1;

            pointer-events: none;

            content: '';

            opacity: 0;

            transition: opacity 0.2s ease-in-out;
        }

        &::after {
            position: absolute;
            z-index: 1;

            pointer-events: none;

            content: '';

            opacity: 0;

            transition: opacity 0.2s ease-in-out;
        }
    }

    &--has-edge-fades#{&}--horizontal {
        &::before {
            top: 0;
            left: 0;

            width: var(--fade-width);
            height: 100%;

            background: linear-gradient(to right, var(--theme-color-surface-primary-default), rgba(255, 255, 255, 0));
        }

        &::after {
            top: 0;
            right: 0;

            width: var(--fade-width);
            height: 100%;

            background: linear-gradient(to left, var(--theme-color-surface-primary-default), rgba(255, 255, 255, 0));
        }
    }

    &--has-edge-fades#{&}--vertical {
        &::before {
            top: 0;
            left: 0;

            width: 100%;
            height: var(--fade-width);

            background: linear-gradient(to bottom, var(--theme-color-surface-primary-default), rgba(255, 255, 255, 0));
        }

        &::after {
            bottom: 0;
            left: 0;

            width: 100%;
            height: var(--fade-width);

            background: linear-gradient(to top, var(--theme-color-surface-primary-default), rgba(255, 255, 255, 0));
        }
    }

    &--can-scroll-start::before {
        opacity: 1;
    }

    &--can-scroll-end::after {
        opacity: 1;
    }
}

.scrollbar {
    position: absolute;
    z-index: $scroll-bar-z-index;

    .scrollable--vertical > & {
        top: var(--offset-top);
        right: 0;

        width: min-content;
        height: calc(100% - var(--offset-top));
        padding: 0 $scrollbar-padding;
    }

    .scrollable--horizontal > & {
        bottom: 0;
        left: 0;

        width: 100%;
        height: min-content;
        padding: $scrollbar-padding 0;
    }

    &__track {
        background-color: var(--theme-color-surface-scroll);
        border-radius: 0.25rem;

        transition: width 0.5s, height 0.5s, background-color 0.5s;

        &:hover {
            cursor: pointer;
        }
    }

    &--active &__track,
    &:hover &__track {
        width: $scrollbar-track-size-on-hover;
    }

    .scrollable--vertical > & > &__track {
        width: $scrollbar-track-size;
    }

    .scrollable--horizontal > & > &__track {
        height: $scrollbar-track-size;
    }
}
</style>
